// Catalog.ID Client — Signed Delegate Invitation (pure)
// ============================================================================
// An invitation is created and signed on the inviting delegate's OWN device
// with that delegate's `main_signing_key` (registration-restructure §13.5); the
// node only verifies it, stores it, and honours it at activation. This module
// is the document itself — DOM-free, network-free, and the single place the
// wire contract pinned on 2026-09-04 is expressed on the client side. The C#
// server (signature verification) and the Rust validator (fixed-value check)
// carry the same contract; every constant below is shared with them.
//
// Wire contract (M15 half pinned 2026-09-04, M18 half 2026-09-07, each before
// any of the three implementations):
//
//   code              INV-XXXX-XXXX-XXXX-XXXX — 16 body characters from the
//                     voucher alphabet (80 bits), CSPRNG on the inviter's
//                     client. Normalised like an RGV- code (upper-case, strip
//                     separators, fold I/L → 1 and O → 0), except that the
//                     prefix is matched BEFORE the fold, since 'I' would fold
//                     itself. THE SERVER NEVER SEES IT.
//   derivation        dk = scrypt(UTF-8(16 body chars, prefix excluded),
//                                 UTF-8("catalog-id invitation v2"),
//                                 N = 2^15, r = 8, p = 1, 96 bytes)
//                     lookup id   = hex_lower(dk[0:32])   ← what the wire carries
//                     value salt  = hex_lower(dk[32:48])  ← never leaves the client
//                     (dk[48:64] unused)
//                     Ed25519 seed = dk[64:96]            ← opens the lock (§4.1)
//                     Round 10 widened dkLen 64 → 96. scrypt's output is PBKDF2
//                     over the mixed block, so dk[0:64] is byte-identical to the
//                     64-byte derivation and every M18 vector survived untouched.
//   secret hash       hex_lower(SHA-256(UTF-8(lookup-id hex STRING))) — what the
//                     node stores; a slow KDF stands behind it, so no pepper.
//   fixed-value hash  hex_lower(SHA-256(UTF-8(salt) ‖ 0x0A ‖ UTF-8(normalised)))
//                     where `salt` is the 32-hex-character STRING — its ASCII
//                     bytes, not the 16 bytes it encodes. Getting that wrong is
//                     the likeliest cross-language divergence, so it is pinned
//                     by a test of its own. UNCHANGED by M18: only the salt's
//                     SOURCE moved, from a CSPRNG in the document to the code.
//   normalisation     email = Unicode NFC → trim → lowercase
//                     phone = E.164: a leading '+' and digits, nothing else
//   nonce             16 CSPRNG bytes as 32 lowercase hex, one per invitation
//   canonical JSON    keys sorted lexicographically at every level, no
//                     whitespace, UTF-8; timestamps RFC 3339 UTC with
//                     milliseconds (`Date.toISOString()`)
//   signed message    UTF-8("catalog-id invitation v1\n") ‖ canonical JSON
//                     — the PREFIX did not move with the version bump; `v`
//                     inside the document is what says which shape it is.
//   signature         hybrid Ed25519 + ML-DSA-65 (`signHybrid`), transported
//                     as the wrapper JSON
//   URL               https://<site>/register#code=INV-… and nothing else
//
// WHY the fixed values are hashed: the node must never see a claim's plaintext
// (§11). The plaintext travels on the card, nowhere else; the validator — which
// holds the plaintext at verification time — is the only party that can compare
// it with `emailHash`/`phoneHash`.
//
// WHY the document has no `salt` (v2): the node stores `documentjson` verbatim
// for audit, so a salt inside it would be the dropped salt column by another
// name, and with it the offline dictionary scan of `emailhash`/`phonehash` that
// §14.2 exists to close. The salt is derived from the code, on the client, by
// whoever holds the card — and by nobody else.

import { sha256 } from '@noble/hashes/sha2.js';
import { scryptAsync } from '@noble/hashes/scrypt.js';
import { ed25519 } from '@noble/curves/ed25519.js';
import { canonicalize, canonicalizeToString, type CanonicalValue } from '@catalog-canonical-json';
import { encodeUtf8, toHex } from '../crypto/aes';
import { signHybrid, verifyHybrid, wrapperToJson, parseWrapper } from '../crypto/signing';

/** The only document version this module builds or reads. v2 dropped `salt`;
 *  v3 (registration-wallets §4.1, D16) replaced the plaintext `voucherCode`
 *  with the voucher's LOOKUP ID. Nothing is live, so v2 is simply gone — the
 *  server refuses a document below v3 and one that carries `voucherCode`. */
export const INVITATION_VERSION = 3 as const;

/** Domain separator prefixed to the canonical JSON before signing. Deliberately
 *  still `v1`: it separates invitation signatures from every other signed
 *  Catalog payload, and `v` inside the document is what distinguishes the two
 *  document shapes. Moving it would invalidate nothing and gain nothing. */
export const INVITATION_SIGNING_PREFIX = 'catalog-id invitation v1\n';

/** Path the printed URL points at — the anonymous entry page (§14.2), NOT the
 *  activation wizard: the entry page routes on the code's prefix. */
export const INVITATION_ENTRY_PATH = '/register';

/** Bytes of randomness behind a nonce (rendered as 32 hex chars). */
const RANDOM_HEX_BYTES = 16;

/** Shape of a lookup id on every route and inside the document: the 32 derived
 *  bytes as 64 LOWERCASE hex characters. Uppercase is refused at the shape
 *  check on both sides — the hash preimage is the string, so the case matters. */
const VOUCHER_LOOKUP_ID_RE = /^[0-9a-f]{64}$/;

// ── the typeable code (§14.2) ───────────────────────────────────────────────

/** Display prefix. Distinct from the voucher's `RGV-`, so the entry page routes
 *  a typed code without a round trip. */
export const INVITATION_CODE_PREFIX = 'INV-';

/** Characters after the prefix: 16 × 5 bits = 80 bits. */
export const INVITATION_CODE_BODY_LENGTH = 16;

/** Crockford Base32, byte-identical to the server's `VoucherCodeService.Alphabet`:
 *  digits plus A–Z minus I, L, O (folded on input) and U. */
export const INVITATION_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Domain-separation salt for the code KDF. CONSTANT on purpose: the invitee's
 * client holds nothing but the code when it needs the lookup id, so there is
 * nothing per-invitation to salt with. Acceptable only because the codes are
 * 80-bit CSPRNG values — never do this for a human-chosen secret.
 */
export const INVITATION_KDF_DOMAIN = 'catalog-id invitation v2';

/** scrypt cost. 2^15 × 8 × 128 B = 32 MiB and ~0.1 s, which is the whole point:
 *  it is what makes a database dump useless for scanning the pending hashes. */
export const INVITATION_KDF_N = 1 << 15;
export const INVITATION_KDF_R = 8;
export const INVITATION_KDF_P = 1;
/** Round 10 (registration-wallets §4.1): 64 → 96, the last 32 bytes being the
 *  Ed25519 seed. Widening is safe by construction — see the header. */
const INVITATION_KDF_DKLEN = 96;

/**
 * Fold a typed code to its canonical compact form (`INV-` + 16 body
 * characters), or null when it is not a well-formed invitation code.
 *
 * The one difference from `normalizeVoucherCode`: the prefix is matched BEFORE
 * the look-alike fold, because 'I' folds to '1' and would mangle `INV` itself.
 * `1NV` is accepted too, so a code transcribed through somebody else's folding
 * still works.
 */
export function normaliseInvitationCode(raw: string | null | undefined): string | null {
    if (!raw || raw.length > 128) return null;

    let compact = '';
    for (const ch of raw) {
        if (ch === ' ' || ch === '-' || ch === '_' || ch === '\t' || ch === '\r' || ch === '\n') continue;
        compact += ch.toUpperCase();
    }
    if (!compact.startsWith('INV') && !compact.startsWith('1NV')) return null;

    let body = '';
    for (const c of compact.slice(3)) {
        body += c === 'I' || c === 'L' ? '1' : c === 'O' ? '0' : c;
    }
    if (body.length !== INVITATION_CODE_BODY_LENGTH) return null;
    for (const c of body) {
        if (!INVITATION_CODE_ALPHABET.includes(c)) return null;
    }
    return INVITATION_CODE_PREFIX + body;
}

/** True when `raw` normalises to a well-formed invitation code. */
export function isInvitationCode(raw: string | null | undefined): boolean {
    return normaliseInvitationCode(raw) !== null;
}

/**
 * The printed form: `INV-XXXX-XXXX-XXXX-XXXX`. Groups of four, because this is
 * read off paper, typed on a phone and dictated over a telephone. Input that
 * does not normalise is returned untouched — never mangle what the user typed.
 */
export function formatInvitationCode(code: string): string {
    const normalised = normaliseInvitationCode(code);
    if (!normalised) return code;
    const body = normalised.slice(INVITATION_CODE_PREFIX.length);
    const groups: string[] = [];
    for (let i = 0; i < body.length; i += 4) groups.push(body.slice(i, i + 4));
    return INVITATION_CODE_PREFIX + groups.join('-');
}

/**
 * A fresh invitation code in DISPLAY form. 10 CSPRNG bytes packed 5 bits at a
 * time into 16 alphabet characters — the whole 80 bits, unbiased, with no
 * remainder to throw away.
 */
export function mintInvitationCode(): string {
    const entropy = crypto.getRandomValues(new Uint8Array(10));
    let bits = 0;
    let acc = 0;
    let body = '';
    for (const byte of entropy) {
        acc = (acc << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            bits -= 5;
            body += INVITATION_CODE_ALPHABET[(acc >>> bits) & 0x1f];
        }
    }
    return formatInvitationCode(INVITATION_CODE_PREFIX + body);
}

/** What one invitation code unlocks, and the ONLY things derived from it. */
export interface InvitationSecrets {
    /** 64 lowercase hex characters. The bearer credential on the wire; the node
     *  stores only {@link invitationSecretHash} of it. */
    lookupIdHex: string;
    /** 32 lowercase hex characters. The fixed-value salt — NEVER sent to the
     *  node; it reaches the validator inside the validator-encrypted material. */
    saltHex: string;
    /** 32 raw bytes — the Ed25519 seed that signs a lock release (§4.1). NEVER
     *  leaves the client and never reaches any server: whoever holds the code
     *  holds the money the invitation carries (D20). */
    seed: Uint8Array;
    /** The seed's 32-byte public half — the lock's CONDITION, and what
     *  {@link lockKeyFor} turns into the lock key. Public by design: the Worker
     *  sees it, and it says nothing about the code. */
    publicKey: Uint8Array;
    /** {@link publicKey} as 64 lowercase hex — the form the Worker's wire uses. */
    publicKeyHex: string;
}

/**
 * The slow half of the contract: one scrypt over the code's 16 body characters
 * under a constant domain salt, split into a lookup id and a value salt.
 *
 * The PREFIX is excluded from the KDF input — it is decoration that routes the
 * entry page, carries no entropy, and would only be one more thing for three
 * implementations to disagree about.
 *
 * Async (`scryptAsync`) so a 32 MiB, ~0.1 s derivation does not freeze the tab.
 */
export async function deriveInvitationSecrets(code: string): Promise<InvitationSecrets> {
    const normalised = normaliseInvitationCode(code);
    if (!normalised) throw new Error('That is not a valid invitation code');
    const body = normalised.slice(INVITATION_CODE_PREFIX.length);
    const dk = await scryptAsync(encodeUtf8(body), encodeUtf8(INVITATION_KDF_DOMAIN), {
        N: INVITATION_KDF_N, r: INVITATION_KDF_R, p: INVITATION_KDF_P, dkLen: INVITATION_KDF_DKLEN,
    });
    // `slice`, not `subarray`: the seed must not be a view onto the whole
    // derived key, or a caller that zeroes it would blank the lookup id too.
    const seed = dk.slice(64, 96);
    const publicKey = ed25519.getPublicKey(seed);
    return {
        lookupIdHex: toHex(dk.subarray(0, 32)),
        saltHex: toHex(dk.subarray(32, 48)),
        seed,
        publicKey,
        publicKeyHex: toHex(publicKey),
    };
}

/**
 * What the node stores as `secrethash`: SHA-256 over the lookup id's 64 HEX
 * CHARACTERS (their ASCII bytes), not over the 32 bytes they encode. Mirrored
 * in C#; pinned by a test on both sides, because it is the one place the two
 * readings would silently diverge.
 *
 * Here so the client can check its own work; the server computes it itself and
 * trusts nothing sent to it.
 */
export function invitationSecretHash(lookupIdHex: string): string {
    return toHex(sha256(encodeUtf8(lookupIdHex)));
}

/**
 * The signed part of an invitation. Serialized with
 * {@link canonicalInvitationJson}; stored verbatim by the node for audit.
 *
 * `emailHash` / `phoneHash` are ABSENT (not null, not empty) when the inviter
 * did not fix that channel — a null would change the canonical bytes and with
 * them the signature.
 */
export interface InvitationDocument {
    v: typeof INVITATION_VERSION;
    /** Organization the invitee joins. */
    orgCid: string;
    /** The owner-role delegate who signed this. */
    inviterCid: string;
    /** Roles the redemption copies onto the delegate row. Order is preserved. */
    roles: string[];
    /** Set only when the invitation fixes the email. */
    emailHash?: string;
    /** Set only when the invitation fixes the phone. */
    phoneHash?: string;
    /** The `delegate` voucher this invitation reserves, by its LOOKUP ID — 64
     *  lowercase hex, `deriveVoucherSecrets(code).lookupIdHex`. v3 (D16): the
     *  node stores `documentjson` verbatim, so the plaintext code that used to
     *  sit here was every delegate voucher code in the clear on the server.
     *
     *  ABSENT — never null, never empty — on a `Payment:Mode=waived` node,
     *  which has no vouchers at all. Presence is the signal: the server refuses
     *  the field on a waived node and requires it on a paid one. */
    voucherLookupId?: string;
    /** 32 lowercase hex characters — replay separation between two otherwise
     *  identical invitations (same org, roles, voucher, second). */
    nonce: string;
    /** RFC 3339 UTC with milliseconds. */
    issuedAt: string;
    /** RFC 3339 UTC with milliseconds; default 30 days out (`Invitation:ExpiryDays`). */
    expiresAt: string;
}

// ── normalisation ───────────────────────────────────────────────────────────

/**
 * Email as it is hashed: Unicode NFC first (so a decomposed 'é' typed on one
 * keyboard matches a composed one typed on another), then trim, then lowercase.
 * The order matters — `toLowerCase()` on decomposed input yields decomposed
 * output, so normalising afterwards would give a different string.
 */
export function normaliseEmail(value: string): string {
    return value.normalize('NFC').trim().toLowerCase();
}

/**
 * Phone as it is hashed: E.164 — a leading '+' if the input had one, then the
 * digits. Spaces, dashes, dots, parentheses and any later '+' are dropped.
 * Deliberately NOT a validator: the number was already accepted by the claim
 * flow; this only has to be deterministic on both sides of the wire.
 */
export function normalisePhone(value: string): string {
    const trimmed = value.trim();
    const digits = trimmed.replace(/\D/g, '');
    return trimmed.startsWith('+') ? `+${digits}` : digits;
}

// ── fixed-value hash ────────────────────────────────────────────────────────

/**
 * `hex_lower(SHA-256(UTF-8(salt) ‖ 0x0A ‖ UTF-8(normalised)))`.
 *
 * `saltHex` is used as a STRING — the 32 hex characters' own ASCII bytes. The
 * 0x0A between the two makes the concatenation unambiguous: without it a salt
 * ending in the value's first characters would collide.
 *
 * @param saltHex     the invitation's `salt`, verbatim
 * @param normalised  output of {@link normaliseEmail} / {@link normalisePhone}
 */
export function hashFixedValue(saltHex: string, normalised: string): string {
    const salt = encodeUtf8(saltHex);
    const value = encodeUtf8(normalised);
    const message = new Uint8Array(salt.length + 1 + value.length);
    message.set(salt, 0);
    message[salt.length] = 0x0a;
    message.set(value, salt.length + 1);
    return toHex(sha256(message));
}

function randomHex(byteLength: number): string {
    return toHex(crypto.getRandomValues(new Uint8Array(byteLength)));
}

/** A fresh per-invitation nonce: 16 CSPRNG bytes as 32 lowercase hex. */
export function newNonce(): string {
    return randomHex(RANDOM_HEX_BYTES);
}

// ── construction ────────────────────────────────────────────────────────────

export interface BuildInvitationInput {
    orgCid: string;
    inviterCid: string;
    roles: readonly string[];
    /** The voucher's lookup id (64 lowercase hex), never its code. Omit on a
     *  waived node, where there is no voucher — the key is then absent from the
     *  signed document, which is what the server reads as "waived". */
    voucherLookupId?: string;
    /** @deprecated v3 refuses it. Declared only so a caller still passing the
     *  pre-round-10 field fails LOUDLY here rather than shipping a plaintext
     *  voucher code into a document the node keeps verbatim (D16). */
    voucherCode?: never;
    /** The fixed-value salt, `deriveInvitationSecrets(code).saltHex`. Hashes the
     *  fixed values below; deliberately NOT stored in the document. */
    valueSalt: string;
    /** Raw, as the inviter typed it — normalised and hashed here. Blank ⇒ not fixed. */
    fixedEmail?: string;
    /** Raw, as the inviter typed it — normalised and hashed here. Blank ⇒ not fixed. */
    fixedPhone?: string;
    now: Date;
    expiresAt: Date;
}

/**
 * Build the document to sign. Mints the nonce, hashes whichever fixed values
 * were given under the CODE-DERIVED salt the caller passes in, and stamps both
 * timestamps in RFC 3339 UTC with milliseconds.
 *
 * The salt itself is NOT part of the result — see the version note at the top.
 */
export function buildInvitationDocument(input: BuildInvitationInput): InvitationDocument {
    if ((input as { voucherCode?: unknown }).voucherCode !== undefined) {
        throw new Error('An invitation document (v3) carries voucherLookupId, never voucherCode: '
            + 'the node stores the document verbatim');
    }
    if (input.voucherLookupId !== undefined && !VOUCHER_LOOKUP_ID_RE.test(input.voucherLookupId)) {
        throw new Error('voucherLookupId must be 64 lowercase hex characters');
    }
    const salt = input.valueSalt;
    const email = input.fixedEmail ? normaliseEmail(input.fixedEmail) : '';
    const phone = input.fixedPhone ? normalisePhone(input.fixedPhone) : '';
    return {
        v: INVITATION_VERSION,
        orgCid: input.orgCid,
        inviterCid: input.inviterCid,
        roles: [...input.roles],
        // Spread-nothing rather than `undefined`: the key must be absent from
        // the object, or `canonicalize` would have to decide what to do with it.
        ...(email ? { emailHash: hashFixedValue(salt, email) } : {}),
        ...(phone ? { phoneHash: hashFixedValue(salt, phone) } : {}),
        // Spread-nothing rather than `undefined`, like the fixed-value hashes:
        // the key must be ABSENT from the canonical bytes on a waived node.
        ...(input.voucherLookupId !== undefined
            ? { voucherLookupId: input.voucherLookupId } : {}),
        nonce: newNonce(),
        issuedAt: input.now.toISOString(),
        expiresAt: input.expiresAt.toISOString(),
    };
}

// ── canonical form & signature ──────────────────────────────────────────────

/**
 * The exact bytes that are signed and stored, as a string. RFC 8785 (JCS) via
 * the shared `libs/canonical-json` serializer — the same one the wrapper JSON
 * and every other Catalog signed payload use, and byte-for-byte equal to the
 * authoritative .NET implementation the server will verify with.
 */
export function canonicalInvitationJson(doc: InvitationDocument): string {
    return canonicalizeToString(doc as unknown as CanonicalValue);
}

/** `UTF-8(prefix) ‖ canonical JSON bytes` — what `signHybrid` actually signs. */
export function invitationSigningMessage(doc: InvitationDocument): Uint8Array {
    const prefix = encodeUtf8(INVITATION_SIGNING_PREFIX);
    const body = canonicalize(doc as unknown as CanonicalValue);
    const message = new Uint8Array(prefix.length + body.length);
    message.set(prefix, 0);
    message.set(body, prefix.length);
    return message;
}

/**
 * Sign with the inviter's `main_signing_key` and return the signature wrapper
 * JSON the API body carries. The caller reads the 64-byte combined seed out of
 * the unlocked key bag (`getKeyBagEntry(bag, 'main_signing_key')`, as
 * `auth/step-up.ts` does) — this module never touches key state.
 */
export function signInvitation(doc: InvitationDocument, mainSigningSeed: Uint8Array): string {
    return wrapperToJson(signHybrid(invitationSigningMessage(doc), mainSigningSeed));
}

/**
 * Verify a wrapper JSON against a document and the signer's combined hybrid
 * public key. Both halves must verify. Used by the tests and by anything on the
 * client that re-checks its own work; the server mirrors this exactly —
 * canonicalize the stored document, prefix, verify with the inviter's active
 * `main_signing_key`.
 */
export function verifyInvitationSignature(
    doc: InvitationDocument, wrapperJson: string, publicKey: Uint8Array,
): boolean {
    const wrapper = parseWrapper(wrapperJson);
    if (!wrapper) return false;
    return verifyHybrid(invitationSigningMessage(doc), wrapper, publicKey);
}

// ── the printed URL ─────────────────────────────────────────────────────────

/** What the card's QR and its printed address carry — the code and, for a
 *  delegate invitation, the organization's CID. The fixed values are on the card
 *  in ink and in the invitation mail; keeping them out of the link is what makes
 *  one invitation behave the same way however the invitee arrived (§14.2). */
export interface InvitationRef {
    /** Canonical compact form, `INV-` + 16 body characters. */
    code: string;
    /**
     * The organization a delegate invitation belongs to (docs/cid-governance.md
     * M7a). The invitation lives only at the organization's origin node, and
     * `begin` names the organization so any node can send the invitee there
     * first. Public — it is printed on the card beside the code.
     */
    orgCid?: string;
}

/** Shape of a CID: ten lower-case letters and digits. */
const CID_SHAPE = /^[a-z0-9]{10}$/;

/**
 * An organization ID as a person types it off the card, or null when it is not
 * one: spaces are dropped and case folded, then the shape is checked BEFORE any
 * request is made with it.
 */
export function normaliseOrgCid(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const folded = raw.replace(/\s+/g, '').toLowerCase();
    return CID_SHAPE.test(folded) ? folded : null;
}

/**
 * Loopback hosts speak plain HTTP — a dev node has no certificate, and an
 * `https://localhost:5100` card would be unusable on the machine it was printed
 * on. Exact hostnames only: `localhost.example.com` is somebody's real domain.
 */
function isLoopbackHost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1'
        || hostname === '[::1]' || hostname === '::1';
}

/**
 * The absolute entry address printed on the card, both as the QR payload and as
 * the typeable line under it.
 *
 * `site` is a host (plus any non-root path base) — `nodeHostFor()`'s output or
 * the node's announced `siteBaseUrl` authority. NEVER `location.origin` inside
 * the desktop dashboard, which is the helper's loopback and does not outlive
 * the desktop process (§11); `activationUrlFor` in `delegate-activation-ref.ts`
 * is what picks between the two and appends `?dp=` where it must.
 *
 * Scheme: the printed sheets print the host WITHOUT a scheme, so there was no
 * existing rule to inherit; a QR payload has to be absolute, so `https` is
 * assumed and only a loopback host is downgraded to `http`.
 *
 * The code rides the FRAGMENT: browsers never send it, so it reaches no access
 * log (the node's nginx logs the full request line) and no Referer — and the
 * node, which by design never sees the code, does not start seeing it because
 * somebody clicked a link.
 */
export function invitationUrl(site: string, code: string, orgCid?: string): string {
    const normalised = normaliseInvitationCode(code);
    if (!normalised) throw new Error('That is not a valid invitation code');
    return entryUrlForCode(site, formatInvitationCode(normalised), orgCid);
}

/**
 * The entry link for ANY typeable code, given in its printed display form: the
 * entry page routes on the prefix, so an `RGV-` voucher card (registration-
 * wallets §7) and an `INV-` invitation card build the same URL shape. Shared
 * from here because this module owns the entry path and the loopback rule.
 *
 * @param site        the announced host, optionally with a path base
 * @param displayCode the code EXACTLY as it is printed beside the QR
 * @param orgCid      a delegate invitation's organization (M7a), carried beside
 *                    the code as `org=`; ignored when it is not a CID
 */
export function entryUrlForCode(site: string, displayCode: string, orgCid?: string): string {
    const host = site.replace(/^\/+|\/+$/g, '');
    const authority = host.split('/')[0] ?? '';
    const hostname = authority.startsWith('[')
        ? authority.slice(0, authority.indexOf(']') + 1)
        : (authority.split(':')[0] ?? '');
    const scheme = isLoopbackHost(hostname) ? 'http' : 'https';
    const org = normaliseOrgCid(orgCid);
    return `${scheme}://${host}${INVITATION_ENTRY_PATH}#code=${displayCode}${org ? `&org=${org}` : ''}`;
}

/**
 * Read an invitation link back. Accepts absolute and relative forms (the entry
 * page parses its own `location`) and ignores parameters it does not know — the
 * dashboard's `?dp=` rides along. The fragment is checked first, then the
 * query, so a link hand-retyped without the `#` still works. Returns null when
 * there is no well-formed code; the path is deliberately NOT checked, since the
 * router has already decided that.
 *
 * The M15 `?id=…#secret=…` form is GONE: a link is a code carrier now, and a
 * parser that still accepted the old shape would keep the retired bearer-secret
 * path alive on the client after the server dropped it.
 */
export function parseInvitationUrl(url: string): InvitationRef | null {
    let parsed: URL;
    try {
        // Base for relative links; never used for anything but parsing.
        parsed = new URL(url, 'https://invitation.invalid');
    } catch {
        return null;
    }
    const fragment = new URLSearchParams(parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash);
    const raw = fragment.get('code')?.trim() || parsed.searchParams.get('code')?.trim() || '';
    const code = normaliseInvitationCode(raw);
    if (!code) return null;
    const orgCid = normaliseOrgCid(fragment.get('org') ?? parsed.searchParams.get('org'));
    return orgCid ? { code, orgCid } : { code };
}
